import { firstValueFrom, timeout } from 'rxjs';
import { PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CanActivateFn, Router } from '@angular/router';
import { MaintenanceWorkspaceService } from './maintenance-workspace.service';
import { MaintenancePortalService, MaintenanceSession } from './maintenance-portal.service';
import { MaintenanceCompanyService } from './maintenance-company.service';
import { CompanySessionService } from './company-session.service';

export const maintenancePortalGuard: CanActivateFn = async (route, state) => {
  if (!isPlatformBrowser(inject(PLATFORM_ID))) {
    return true;
  }

  const workspace = inject(MaintenanceWorkspaceService);
  const api = inject(MaintenancePortalService);
  const router = inject(Router);
  try {
    if (state.url.split('?')[0] === '/dashboard') {
      const session = await firstValueFrom(api.verifySession().pipe(timeout(8000)));
      // The root and unknown URLs land here; a maintenance company has no workspace, so send it to its own pages.
      if (session?.authenticated && isCompanyAccount(session)) {
        const resolved = await workspace.resolveSession().catch(() => null);
        return resolved && workspace.currentWorkspace() ? true : router.createUrlTree(['/company']);
      }
      if (session?.authenticated) return true;
    }
    const resolved = await workspace.resolveSession();
    if (resolved && workspace.currentWorkspace()) {
      const permission = route.data['permission'] as string | undefined;
      return !permission || workspace.allows(permission) ? true : router.createUrlTree(['/dashboard'], { queryParams: { accessDenied: true } });
    }
  } catch (error) {
    console.warn('[MaintenancePortalGuard] maintenance session lookup failed:', error);
  }

  return router.createUrlTree(['/login'], {
    queryParams: state.url && state.url !== '/login' ? { returnUrl: state.url } : undefined,
  });
};

/** Any signed-in account (a maintenance company has no workspace); otherwise the login page, coming back afterwards. */
export const signedInGuard: CanActivateFn = async (_route, state) => {
  if (!isPlatformBrowser(inject(PLATFORM_ID))) {
    return true;
  }
  const api = inject(MaintenancePortalService);
  const router = inject(Router);
  const session = await firstValueFrom(api.verifySession().pipe(timeout(8000))).catch(() => null);
  return session?.authenticated ? true : router.createUrlTree(['/login'], { queryParams: { returnUrl: state.url } });
};

/** Pages only the company's admin uses (the team); a technician goes back to the company dashboard. */
export const companyAdminGuard: CanActivateFn = async () => {
  if (!isPlatformBrowser(inject(PLATFORM_ID))) {
    return true;
  }
  const session = inject(CompanySessionService);
  const companies = inject(MaintenanceCompanyService);
  const router = inject(Router);
  if (!session.company()) {
    // The company shell loads this too, but child guards run before it does.
    const company = await firstValueFrom(companies.myCompany()).catch(() => null);
    if (company) session.company.set(company);
  }
  return session.isAdmin() ? true : router.createUrlTree(['/company']);
};

function isCompanyAccount(session: MaintenanceSession): boolean {
  return (session.roles ?? []).includes('ROLE_MAINTENANCE_COMPANY')
    || String(session.accountType ?? '').toUpperCase() === 'MAINTENANCE_COMPANY';
}
